"use client";

import { useState, useCallback } from "react";
import { Loader2, Building2, User, MapPin, Save, Phone, FileSignature, Mail, Landmark } from "lucide-react";
interface LegalEntityAddressFormProps {
  role: "seller" | "buyer";
  initialValues?: {
    company?: string | null;
    name?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
    taxId?: string | null;
    phone?: string | null;
    iban?: string | null;
    signerName?: string | null;
    signerEmail?: string | null;
  };
  onSubmit: (values: {
    company?: string;
    name: string;
    addressLine1: string;
    addressLine2?: string;
    postalCode: string;
    city: string;
    country: string;
    taxId?: string;
    phone?: string;
    iban?: string;
    signerName?: string;
    signerEmail?: string;
  }) => Promise<void>;
  disabled?: boolean;
  className?: string;
}

/**
 * LegalEntityAddressForm - Form for entering the legal entity / contract address.
 *
 * Used during the identity verification step so that addresses are available
 * before the contract is generated. Unlike BillingAddressForm, this does NOT
 * include terms acceptance (that stays in the contract review step).
 */
export function LegalEntityAddressForm({
  role,
  initialValues,
  onSubmit,
  disabled = false,
  className = ""
}: LegalEntityAddressFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [company, setCompany] = useState(initialValues?.company ?? "");
  const [name, setName] = useState(initialValues?.name ?? "");
  const [addressLine1, setAddressLine1] = useState(initialValues?.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(initialValues?.addressLine2 ?? "");
  const [postalCode, setPostalCode] = useState(initialValues?.postalCode ?? "");
  const [city, setCity] = useState(initialValues?.city ?? "");
  const [country] = useState(initialValues?.country ?? "DE");
  const [taxId, setTaxId] = useState(initialValues?.taxId ?? "");
  const [phone, setPhone] = useState(initialValues?.phone ?? "");
  const [iban, setIban] = useState(initialValues?.iban ?? "");
  const [signerName, setSignerName] = useState(initialValues?.signerName ?? "");
  const [signerEmail, setSignerEmail] = useState(initialValues?.signerEmail ?? "");
  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = "Name ist erforderlich";
    }
    if (!addressLine1.trim()) {
      newErrors.addressLine1 = "Adresse ist erforderlich";
    }
    if (!postalCode.trim() || postalCode.length < 4) {
      newErrors.postalCode = "Gültige PLZ erforderlich";
    }
    if (!city.trim()) {
      newErrors.city = "Stadt ist erforderlich";
    }
    if (role === "seller" && !taxId.trim()) {
      newErrors.taxId = "Steuernummer ist erforderlich";
    }
    if (role === "seller" && !iban.trim()) {
      newErrors.iban = "IBAN ist erforderlich";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, addressLine1, postalCode, city, role, taxId, iban]);
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        company: company.trim() || undefined,
        name: name.trim(),
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim() || undefined,
        postalCode: postalCode.trim(),
        city: city.trim(),
        country,
        taxId: taxId.trim() || undefined,
        phone: phone.trim() || undefined,
        iban: iban.trim() || undefined,
        signerName: signerName.trim() || undefined,
        signerEmail: signerEmail.trim() || undefined
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [validate, onSubmit, company, name, addressLine1, addressLine2, postalCode, city, country, taxId, phone, iban, signerName, signerEmail]);
  const roleLabel = role === "seller" ? "Verkäufer" : "Käufer";
  return <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-5 h-5 text-zinc-400" />
        <h3 className="font-medium text-zinc-900">Vertragsadresse {roleLabel}</h3>
      </div>

      {/* Company (optional) */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
          <Building2 className="w-4 h-4" />
          Firma (optional)
        </label>
        <input type="text" value={company} onChange={e_0 => setCompany(e_0.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder="GmbH, AG, etc." />
      </div>

      {/* Name */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
          <User className="w-4 h-4" />
          Name *
        </label>
        <input type="text" value={name} onChange={e_1 => setName(e_1.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.name ? "border-red-300" : "border-zinc-300"}`} placeholder="Max Mustermann" />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      {/* Address Line 1 */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
          <MapPin className="w-4 h-4" />
          Adresse *
        </label>
        <input type="text" value={addressLine1} onChange={e_2 => setAddressLine1(e_2.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.addressLine1 ? "border-red-300" : "border-zinc-300"}`} placeholder="Musterstraße 123" />
        {errors.addressLine1 && <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>}
      </div>

      {/* Address Line 2 (optional) */}
      <div>
        <label className="text-sm font-medium text-zinc-700 mb-1 block">
          Adresszusatz (optional)
        </label>
        <input type="text" value={addressLine2} onChange={e_3 => setAddressLine2(e_3.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder="Gebäude B, 3. Stock" />
      </div>

      {/* PLZ and City */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">PLZ *</label>
          <input type="text" value={postalCode} onChange={e_4 => setPostalCode(e_4.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.postalCode ? "border-red-300" : "border-zinc-300"}`} placeholder="12345" />
          {errors.postalCode && <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>}
        </div>
        <div className="col-span-2">
          <label className="text-sm font-medium text-zinc-700 mb-1 block">Stadt *</label>
          <input type="text" value={city} onChange={e_5 => setCity(e_5.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.city ? "border-red-300" : "border-zinc-300"}`} placeholder="Berlin" />
          {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
        </div>
      </div>

      {/* Country (fixed to Germany for now) */}
      <div>
        <label className="text-sm font-medium text-zinc-700 mb-1 block">Land</label>
        <input type="text" value="Deutschland" disabled className="w-full px-3 py-2 border border-zinc-300 rounded-lg bg-zinc-100 text-zinc-500" />
      </div>

      {/* Phone (optional) */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
          <Phone className="w-4 h-4" />
          Telefon (optional)
        </label>
        <input type="tel" value={phone} onChange={e_6 => setPhone(e_6.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder="+49 170 1234567" />
      </div>

      {/* Tax ID (required for sellers only) */}
      {role === "seller" && <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">Steuernummer *</label>
          <input type="text" value={taxId} onChange={e_7 => setTaxId(e_7.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.taxId ? "border-red-300" : "border-zinc-300"}`} placeholder="12345678901" />
          {errors.taxId && <p className="mt-1 text-sm text-red-600">{errors.taxId}</p>}
        </div>}

      {/* IBAN (seller only) */}
      {role === "seller" && <div>
          <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
            <Landmark className="w-4 h-4" />
            IBAN *
          </label>
          <input type="text" value={iban} onChange={e_8 => setIban(e_8.target.value.toUpperCase())} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.iban ? "border-red-300" : "border-zinc-300"}`} placeholder="DE89 3704 0044 0532 0130 00" />
          {errors.iban && <p className="mt-1 text-sm text-red-600">{errors.iban}</p>}
          <p className="mt-1 text-xs text-zinc-400">
            Für die Auszahlung nach Abschluss des Vorgangs
          </p>
        </div>}

      {/* Signer (Vertragspartner) */}
      <div className="border-t border-zinc-200 pt-4 mt-2">
        <div className="flex items-center gap-2 mb-3">
          <FileSignature className="w-4 h-4 text-zinc-400" />
          <h4 className="text-sm font-medium text-zinc-700">Vertragspartner (Unterzeichner)</h4>
        </div>
        <p className="text-xs text-zinc-500 mb-3">
          Person, die den Kaufvertrag unterschreibt. Falls abweichend vom Kontoinhaber.
        </p>
        <div className="space-y-3">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
              <User className="w-4 h-4" />
              Name Unterzeichner
            </label>
            <input type="text" value={signerName} onChange={e_9 => setSignerName(e_9.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder={name || "Max Mustermann"} />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
              <Mail className="w-4 h-4" />
              E-Mail Unterzeichner
            </label>
            <input type="email" value={signerEmail} onChange={e_10 => setSignerEmail(e_10.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder="name@firma.de" />
          </div>
        </div>
      </div>

      {/* Submit button */}
      <button type="submit" disabled={disabled || isSubmitting} className="w-full px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
        {isSubmitting ? <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Wird gespeichert...
          </> : <>
            <Save className="w-4 h-4" />
            Adresse speichern
          </>}
      </button>
    </form>;
}